import { Bot, ChevronDown, ChevronRight, Cpu } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";

import type { MessageAttachment } from "@/api/types";
import { cn } from "@/lib/utils";

import { AttachmentBubble } from "./AttachmentBubble";

interface Props {
  role: "user" | "assistant" | "system_event";
  content: string;
  streaming?: boolean;
  /**
   * Em team mode, slug do subagent autor da bolha. Renderiza um header
   * pequeno "@<slug>" no topo + visual mais discreto (borda tracejada)
   * para diferenciar do balao do supervisor.
   */
  agentSlug?: string | null;
  /**
   * Anexos do usuario amarrados a esta mensagem (M3). Renderizados
   * acima do conteudo textual.
   */
  attachments?: MessageAttachment[];
  /**
   * M8: snapshot do modelo que respondeu esta bolha (chat direto).
   * Quando presente, renderiza um chip pequeno no canto superior
   * direito do balao (ex.: "openai · gpt-4o"). Em direct/team mode
   * fica undefined (modelo do agente vai no proprio header da pagina).
   */
  llmModelLabel?: string;
}

export function MessageBubble({
  role,
  content,
  streaming = false,
  agentSlug,
  attachments,
  llmModelLabel,
}: Props) {
  const { t } = useTranslation("threads");
  const alignment = role === "user" ? "ml-auto bg-primary/10" : "bg-card";
  const isSystem = role === "system_event";
  const isSubagent = role === "assistant" && Boolean(agentSlug);
  const showModelChip =
    role === "assistant" && !isSubagent && Boolean(llmModelLabel);
  // Auto-collapse: enquanto o subagent esta escrevendo (streaming=true)
  // o card fica expandido por default; quando termina (streaming=false),
  // colapsa. O usuario pode togglar manualmente -- preferencia local
  // persiste enquanto o componente esta montado.
  const [userToggled, setUserToggled] = useState<boolean | null>(null);
  const autoExpanded = streaming;
  const expanded = userToggled !== null ? userToggled : autoExpanded;
  const subagentCollapsedHeader = isSubagent && !expanded;

  if (subagentCollapsedHeader) {
    const preview = (content || "").trim().split("\n")[0]?.slice(0, 80);
    return (
      <button
        type="button"
        onClick={() => setUserToggled(true)}
        className={cn(
          "flex w-full max-w-[85%] items-center gap-2 rounded-md border border-dashed",
          "border-muted-foreground/30 bg-muted/20 px-3 py-2 text-left",
          "text-xs text-muted-foreground transition-colors hover:bg-muted/40",
        )}
        title={t("subagent.expand", "Expandir resposta do subagente")}
      >
        <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        <Bot className="h-3 w-3 shrink-0" />
        <span className="font-medium text-foreground/80">@{agentSlug}</span>
        <span className="shrink-0">·</span>
        <span className="truncate">
          {preview ||
            t("subagent.completed", "resposta concluida")}
        </span>
      </button>
    );
  }

  return (
    <div
      className={cn(
        "max-w-[85%] rounded-md border p-3 text-sm",
        alignment,
        isSystem && "border-dashed text-muted-foreground italic",
        isSubagent &&
          "border-dashed border-muted-foreground/30 bg-muted/20",
      )}
    >
      {isSubagent ? (
        <button
          type="button"
          onClick={() => setUserToggled(false)}
          className="mb-1 flex w-full items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
          title={t("subagent.collapse", "Recolher resposta do subagente")}
        >
          <ChevronDown className="h-3 w-3" />
          <Bot className="h-3 w-3" />
          <span className="font-medium">@{agentSlug}</span>
          <span>· {t("subagent.label", "subagente")}</span>
        </button>
      ) : null}
      {showModelChip ? (
        <div
          className="mb-1 flex items-center justify-end gap-1 text-[10px] text-muted-foreground"
          title={llmModelLabel ?? undefined}
        >
          <Cpu className="h-3 w-3" />
          <span className="font-medium">{llmModelLabel}</span>
        </div>
      ) : null}
      {attachments && attachments.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-2">
          {attachments.map((a) => (
            <AttachmentBubble
              key={a.id}
              attachmentId={a.id}
              filename={a.filename}
              mimeType={a.mime_type}
              sizeBytes={a.size_bytes}
              kind="input"
            />
          ))}
        </div>
      ) : null}
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            code({ inline, className, children, ...props }: {
              inline?: boolean;
              className?: string;
              children?: React.ReactNode;
            }) {
              const match = /language-(\w+)/.exec(className || "");
              if (inline || !match) {
                return (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              }
              return (
                <SyntaxHighlighter
                  language={match[1]}
                  style={oneLight}
                  PreTag="div"
                >
                  {String(children).replace(/\n$/, "")}
                </SyntaxHighlighter>
              );
            },
          }}
        >
          {content || (streaming ? "..." : "")}
        </ReactMarkdown>
      </div>
    </div>
  );
}
